import { Component, Event, EventEmitter, Host, Prop, State, Watch, h } from '@stencil/core';
import clsx from 'clsx';

@Component({
  tag: 'popup-modal',
  styleUrl: 'popup-modal.scss',
  shadow: true
})
export class PopupModal {
  @Prop() size: 'md' | 'xl' | '2xl' | '3xl' = '3xl';
  @Prop() open: boolean = false;
  @Prop() escapeClose: boolean = false;

  @State() shouldRender: boolean = false;
  @State() animateLeave: boolean = false;

  @Event({ bubbles: false }) popupClose: EventEmitter<void>;

  private closeTimeout: ReturnType<typeof setTimeout>;

  sizes = {
    'md': 'sm:max-w-96',
    'xl': 'sm:max-w-[37rem]',
    '2xl': 'md:max-w-2xl',
    '3xl': 'md:max-w-3xl'
  };

  @Watch('open')
  handleOpenChange(newVal: boolean) {
    if (newVal) {
      clearTimeout(this.closeTimeout);
      this.shouldRender = true;
      this.animateLeave = false;
      document.documentElement.style.overflow = 'hidden';
      window.addEventListener('keydown', this.handleEsc, { passive: true });
    } else {
      document.documentElement.style.overflow = '';
      window.removeEventListener('keydown', this.handleEsc);
      this.animateLeave = true;
      this.closeTimeout = setTimeout(() => {
        this.shouldRender = false;
      }, 200);
    }
  }

  private handleEsc = (e: KeyboardEvent) => {
    if ((e.key === 'Escape' || e.key === 'Esc') && this.escapeClose) {
      this.popupClose.emit();
    }
  };

  connectedCallback() {
    if (this.open) {
      document.documentElement.style.overflow = 'hidden';
      window.addEventListener('keydown', this.handleEsc, { passive: true });
      this.shouldRender = true;
    }
  }

  disconnectedCallback() {
    document.documentElement.style.overflow = '';
    window.removeEventListener('keydown', this.handleEsc);
    clearTimeout(this.closeTimeout);
  }

  render() {
    if (!this.shouldRender) return null;

    const classes = clsx(
      this.sizes[this.size],
      'relative transform overflow-hidden rounded-t-[20px] md:rounded-xl bg-white text-left shadow-xl transition-all md:my-8 w-full',
      this.animateLeave ? 'popup-leave' : 'popup-enter'
    );

    return (
      <Host>
        <div class="relative popup-disable-scroll" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div class="fixed inset-0 h-dvh z-130 bg-[#000000E5] backdrop-blur-[2px] transition-opacity" aria-hidden="true"></div>
          <div class="fixed inset-0 h-dvh z-140 w-screen overflow-y-hidden">
            <div class="flex min-h-full items-end justify-center md:p-4 text-center md:items-center md:p-0">
              <div class={classes}>
                <div class="">
                  <slot />
                </div>
              </div>
            </div>
          </div>
        </div>
      </Host>
    );
  }
}
